import React from "react";
import AdminSelect from "./AdminSelect";
import {
  resolveLayout,
  HERO_POSITION_OPTIONS,
  HERO_WIDTH_OPTIONS,
  CORNER_OPTIONS,
  FORM_ALIGN_OPTIONS,
} from "../utils/signupLayout";

/**
 * Structure controls for the public signup — whether the branded hero panel is
 * shown at all, which side it sits on, how wide it is, what it contains, plus
 * the shell's corner treatment and form alignment. Writes the whole resolved
 * object back to `venue.signup_layout` on every change (all keys explicit), so
 * the stored value is always complete and self-describing.
 *
 * Shared by the admin + client Branding editors.
 *
 *   value:    venue.signup_layout (object | null)
 *   onChange: (nextLayout) => void
 */
function Toggle({ label, hint, checked, disabled, onChange }) {
  return (
    <div className="form-field" style={{ opacity: disabled ? 0.5 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{label}</span>
        <label className="toggle-switch" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
          <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
          <span className="toggle-track" />
        </label>
      </div>
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

export default function SignupLayoutControls({ value, onChange }) {
  const l = resolveLayout({ signup_layout: value });
  const set = (patch) => onChange({ ...l, ...patch });
  const heroOff = !l.heroEnabled;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <Toggle
        label="Show hero panel"
        hint="The branded side panel with your logo, headline and value props. Turn off for a clean, single-column signup."
        checked={l.heroEnabled}
        onChange={(v) => set({ heroEnabled: v })}
      />

      <div style={{ borderTop: "1px solid var(--border)", margin: "0.4rem 0 0.7rem" }} />

      <div className="form-grid-2">
        <div className="form-field" style={{ margin: 0, opacity: heroOff ? 0.5 : 1 }}>
          <label>Hero side <span className="optional">— desktop</span></label>
          <AdminSelect value={l.heroPosition} onChange={(v) => set({ heroPosition: v })} options={HERO_POSITION_OPTIONS} disabled={heroOff} />
        </div>
        <div className="form-field" style={{ margin: 0, opacity: heroOff ? 0.5 : 1 }}>
          <label>Hero width</label>
          <AdminSelect value={l.heroWidth} onChange={(v) => set({ heroWidth: v })} options={HERO_WIDTH_OPTIONS} disabled={heroOff} />
        </div>
      </div>

      <div className="form-grid-2" style={{ marginTop: "0.4rem" }}>
        <Toggle label="Show logo" checked={l.heroShowLogo} disabled={heroOff} onChange={(v) => set({ heroShowLogo: v })} />
        <Toggle label="Show benefits" checked={l.heroShowBenefits} disabled={heroOff} onChange={(v) => set({ heroShowBenefits: v })} />
      </div>

      <div className="form-grid-2">
        <Toggle label="Gradient wash" hint="Off = flat accent fill." checked={l.heroGradient} disabled={heroOff} onChange={(v) => set({ heroGradient: v })} />
        <div className="form-field" style={{ margin: 0, opacity: heroOff ? 0.5 : 1 }}>
          <label>Background image strength <span className="optional">— {Math.round(l.heroCoverOpacity * 100)}%</span></label>
          <input type="range" min={0} max={60} step={2} value={Math.round(l.heroCoverOpacity * 100)} disabled={heroOff} onChange={(e) => set({ heroCoverOpacity: Number(e.target.value) / 100 })} />
          <span className="field-hint">How strongly the hero background image shows through.</span>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", margin: "0.5rem 0 0.7rem" }} />

      <div className="form-grid-2">
        <div className="form-field" style={{ margin: 0 }}>
          <label>Corner style</label>
          <AdminSelect value={l.corner} onChange={(v) => set({ corner: v })} options={CORNER_OPTIONS} />
        </div>
        <div className="form-field" style={{ margin: 0 }}>
          <label>Form alignment <span className="optional">— desktop</span></label>
          <AdminSelect value={l.formAlign} onChange={(v) => set({ formAlign: v })} options={FORM_ALIGN_OPTIONS} />
        </div>
      </div>
    </div>
  );
}
